import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LOGO } from "../lib/api";
import { SOLUTIONS } from "../data/content";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Business Finance", to: "/business-finance" },
  { label: "Solutions", to: "/solutions", dropdown: true },
  { label: "Knowledge Hub", to: "/knowledge-hub" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#1A1A1A]/90 backdrop-blur-md border-b border-[#808080]/20" : "bg-transparent"}`}
    >
      <nav className="container-x mx-auto max-w-[1400px] flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src={LOGO} alt="DNA Finance" className="h-12 w-auto object-contain" />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {NAV.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="relative" onMouseEnter={() => setSolOpen(true)} onMouseLeave={() => setSolOpen(false)}>
                <Link to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-lime transition-colors">
                  {item.label} <ChevronDown className="w-4 h-4" />
                </Link>
                {solOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                    <div className="w-[520px] grid grid-cols-2 gap-1 bg-[#111] border border-[#808080]/30 p-3">
                      {SOLUTIONS.map((s) => (
                        <Link key={s.slug} to={`/solutions/${s.slug}`} data-testid={`nav-dropdown-${s.slug}`} className="flex items-center gap-3 p-3 hover:bg-[#D8FF00]/10 transition-colors group">
                          <s.icon className="w-5 h-5 text-lime shrink-0" />
                          <span className="text-sm text-white/90 group-hover:text-lime">{s.title}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link key={item.label} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`} className={`text-sm font-medium transition-colors ${location.pathname === item.to ? "text-lime" : "text-white/80 hover:text-lime"}`}>
                {item.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <button onClick={() => navigate("/contact?src=nav")} data-testid="nav-get-funding-btn" className="bg-lime text-[#1A1A1A] text-sm font-bold px-6 py-3 hover:glow-lime-strong transition-shadow duration-300">
            Get Funding
          </button>
        </div>

        <button data-testid="nav-mobile-toggle" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-[#111] border-t border-[#808080]/20 px-6 py-6 space-y-4">
          {NAV.map((item) => (
            <Link key={item.label} to={item.to} className="block text-lg font-medium text-white/90 hover:text-lime">{item.label}</Link>
          ))}
          <button onClick={() => navigate("/contact?src=mobile-nav")} data-testid="mobile-get-funding-btn" className="w-full bg-lime text-[#1A1A1A] font-bold py-4 mt-2">Get Funding</button>
        </div>
      )}
    </header>
  );
}
